import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { AdsType, GoogleTextAdService } from './google-text-ad.service';

import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    input,
    signal,
    DOCUMENT,
    inject,
} from '@angular/core';
import { AppGlobalsModule, AppGlobalsModule as _globals } from 'src/app/app-globals.module';
import { StorageService } from 'src/app/core/services/storage/storage.service';
import { I18nService } from 'src/app/core/services/i18n/i18n.service';
import { Observable, Subscription } from 'rxjs';
import { UtilService } from 'src/app/core/services/util/util.service';
import { UrlAddressService, AngularPages } from 'src/app/core/services/url-address/url-address.service';
import { BrowserPlatformService } from 'src/app/core/services/browser-platform/browser-platform.service';

@Component({
    selector: 'google-text-ad',
    templateUrl: './google-text-ad.component.html',
    styleUrls: ['./google-text-ad.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, MatIconModule],
})
export class GoogleTextAdComponent implements OnInit, OnDestroy, AfterViewInit {
    private _document = inject<Document>(DOCUMENT, { optional: true })!;
    private _i18n = inject(I18nService);
    private _adService = inject(GoogleTextAdService);
    private _util = inject(UtilService);
    private storageService = inject(StorageService, { optional: true })!;
    elRef = inject(ElementRef);
    url = inject(UrlAddressService);
    zone = inject(NgZone);
    private urlAddressService = inject(UrlAddressService);
    private _browser = inject(BrowserPlatformService);

    public AngularPages = AngularPages;
    private YESHIVA_APP_ID = '108500498';
    public isAdLoaded = signal(false);
    public tagCode = signal('');
    public globals = AppGlobalsModule;
    //usage
    //dimensions (adWidth/adHeight) are required
    //adCode - It is required. If autoLang == true then heb/eng will be added to the end of adCode
    //autoLang - As mentioned will allow the full adCode to be set without the lang being added
    public adWidth = signal(NaN);
    public adHeight = signal(NaN);
    public adCode = input.required<string>();
    public resolvedAdCode = signal('');
    public closeable = input(false);
    public sideAd = input(false);
    public adStickyFooter = input(false);
    public adEnable = signal(true);
    private static _firstTime = true;

    // Google ad slot needed to manage via GoogleTag Object
    slot: any;

    get isServer(): boolean {
        return _globals.isServer;
    }
    @Output() newItemEvent = new EventEmitter<boolean>();
    @Output() adinitLoad = new EventEmitter();
    @Output() adLoaded = new EventEmitter<boolean>();

    public style: any;

    ngOnInit() {
        if (_globals.isServer) {
            // Server shoudln't serve ads
            return;
        }
        this.adinitLoad.emit();
        this.tagCode.set(this._adService.getAdTag());
        this.resolvedAdCode.set(this.adCode());

        if (!this.adHeight() || !this.adWidth()) {
            let dimensions = this._adService.getAdDimensions(this.resolvedAdCode());
            this.adHeight.set(dimensions.height);
            this.adWidth.set(dimensions.width);
        }

        // Add languages suporrt, exept for codes who doens't have lang support
        if (
            !this.resolvedAdCode().endsWith(this._i18n.getLang()) &&
            this.resolvedAdCode() !== AdsType.MobileRectangle &&
            this.resolvedAdCode() !== AdsType.BigWideVerticalBanner
        ) {
            if (!this.resolvedAdCode().endsWith('-')) {
                this.resolvedAdCode.update((code) => code + '-');
            }
            this.resolvedAdCode.update((code) => code + this._i18n.getLang());
        }

        // If ad has shown in the last two minutes, don't show it.
        if (!_globals.isServer && this.closeable()) {
            let recently = this._adService.CheckAdViewRecently(this.adStickyFooter() ? 'adStickyFooter' : this.tagCode());
            if (recently) {
                this.adEnable.set(false);
                this.newItemEvent.emit(false);
            }
        }
    }

    ngAfterViewInit(): void {
        if (_globals.isServer) {
            // Server shoudln't serve ads
            return;
        }
        let that = this; // Sadly we support ES5 :)
        const win = this._browser.nativeWindow as any;

        if (!win.googletag) {
            // GoogleTextAdComponent._firstTime = true;
            console.log('we set first time only once and if needed');
        }

        win.googletag = win.googletag || { cmd: [] };

        if (!win.googletag) {
            // console.log(`Googletag API isn't ready???`);
            return;
        }

        win.googletag.cmd.push(() => {
            // If it called many times after the initiation it shows warning in the console.
            if (GoogleTextAdComponent._firstTime) {
                GoogleTextAdComponent._firstTime = false;

                if (!win.googletag.pubadsReady) {
                    win.googletag.setConfig({
                        singleRequest: true,
                        disableInitialLoad: true,
                        lazyLoad: {
                            fetchMarginPercent: 200, // Fetch slots within x viewports.
                            renderMarginPercent: 135, // Render slots within x viewports.
                            mobileScaling: 1.2, // Multiple the above values on mobile.
                        },
                    });
                    win.googletag.enableServices();
                }
            }

            that.slot = win.googletag
                .defineSlot(`/${that.YESHIVA_APP_ID}/${that.resolvedAdCode()}`, [that.adWidth(), that.adHeight()], that.tagCode())
                .addService(win.googletag.pubads());

            // This is extremly ungly and shouldn't pass code review.
            // Change Billboard to use the new targeting for Tfilin beit El's campain.
            if (this.resolvedAdCode().includes(AdsType.Billboard) && this.urlAddressService.getURL().includes('calendar/timesday')) {
                let targeting = 'area';
                let value = 'calendar';
                that.slot.setConfig({
                    targeting: {
                        [targeting]: value,
                    },
                });
            }

            // It should be delayed to give priority and bandwidth for solw mobile, so photos and media load earlier.
            _globals.setTimeoutIsNotServer(() => {
                win.googletag.cmd.push(() => {
                    win.googletag.display(that.tagCode());
                    this.refreshAd();
                });
            }, 50);

            // // Register event handlers to observe lazy loading behavior.
            // win.googletag.pubads().addEventListener('slotRequested', function(event) {
            // });

            if (!_globals.isServer) {
                //
                win.googletag.pubads().addEventListener('slotRenderEnded', (ev: any) => {
                    // On load happened for all the ads every event
                    if (ev.slot.getSlotElementId() === this.tagCode()) {
                        // Due to bug that cause ad sometimes to not show from another campeign
                        if (ev.isEmpty) {
                            console.warn(`Ad ${this.tagCode()} is empty , size ${this.adWidth()}x${this.adHeight()}, disabling it.`);
                            if (this.closeable()) {
                                this.closeAdContainer();
                            }
                            this.adLoaded.emit(false);
                        } else {
                            this.adLoaded.emit(true);
                        }
                        this.isAdLoaded.set(true);
                    }
                });

                // Do not ever uncomment this, I have only keep it to set an example for what NOT to do!
                // I know you wonder why I didn't explain, well if you don't understand you better read it again.
                //     // In client we want to animate the result
                //     this.zone.runOutsideAngular(() => {
                //         this._checkIsIframe$ = interval(100).pipe(
                //             tap(_ => {
                //                 if (this._getIsAd()) {
                //                     this.isAdLoaded.set(true);
                //                     this.adLoaded.emit();
                //                     this._util.unsubscribe([this._checkIsIframeSubscription]);
                //                 }
                //             })
                //         );
                //     });

                //     this._checkIsIframeSubscription = this._checkIsIframe$.subscribe();
            }
        });

        // No more needed since it's manually loaded now after display
        // // Activate the ad only if it's already loaded
        // if (win.document.readyState === 'complete') {
        //     // this.refreshAd();
        // } else {
        //     // Ads service will initiate all the ads when page has completed event, after that loading is manually
        // }
    }

    private _checkIsIframeSubscription: Subscription | undefined;
    private _checkIsIframe$: Observable<number> | undefined;

    private _getIsAd(): boolean {
        return this.elRef.nativeElement && this.elRef.nativeElement.getElementsByTagName('iframe')[0];
    }

    ngOnDestroy(): void {
        this._util.unsubscribe([this._checkIsIframeSubscription]);

        if (this.newItemEvent) {
            this.newItemEvent.unsubscribe();
        }
        if (this.adinitLoad) {
            this.adinitLoad.unsubscribe();
        }
        if (this.adLoaded) {
            this.adLoaded.unsubscribe();
        }
        if (_globals.isServer) {
            // Server shoudln't serve ads
            return;
        }
        // console.log(`Ad ${this.tagCode} has destroyed`)
        const win = this._browser.nativeWindow as any;

        // This shouldn't be in cmd push since it must by synchronic, otherwise google load the compounent before it has .
        if (win?.googletag && this.slot) {
            win.googletag.destroySlots([this.slot]);
        }
    }

    refreshAd(): void {
        this.zone.runOutsideAngular(() => {
            const win = this._browser.nativeWindow as any;

            // Ad blockers prevent pubads from initiate.
            if (win?.googletag?.pubads && this.slot) {
                win.googletag.pubads().refresh([this.slot]);
            }
        });
    }

    closeAdContainer(event?: MouseEvent) {
        const closeButton = event?.currentTarget;
        if (closeButton instanceof HTMLElement) {
            closeButton.animate(
                [
                    {
                        opacity: 1,
                        transform: 'translate(-50%, -100%)',
                        visibility: 'visible',
                    },
                    {
                        opacity: 0,
                        transform: 'translate(-50%, -100%)',
                        visibility: 'hidden',
                    },
                ],
                {
                    duration: 1000,
                    easing: 'ease',
                    fill: 'forwards',
                },
            );
        }

        this.adEnable.set(false);
        if (this.closeable() && this.adStickyFooter()) {
            this.storageService.storeData('adStickyFooter', Date.now().toString());
        } else {
            this.storageService.storeData(this.tagCode(), Date.now().toString());
        }

        // Tell parent compounent that the ad has closed
        this.newItemEvent.emit(false);
    }
}
